import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faCheck,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import SEO from "../../components/seo/SEO";
import {
  listYears,
  getYearByIdAction,
  updateYearAction,
} from "../../actions/yearActions";
import {
  ValuesIcon,
  QuoteIconV2,
  VisionIcon,
} from "../../components/ui/Icons/Icon";
import "./values.css";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const TOTAL_STEPS = 8;

const HELP_ITEMS = [
  {
    question: "What is a trait?",
    answer:
      "A trait is a distinguishing quality or characteristic that shapes how you think, feel, and act. Think of it as a consistent pattern in who you are. For example, if you regularly and naturally put yourself in other people's shoes before making decisions - and take kinder, more compassionate actions as a result - you could be said to have the trait of 'empathy'. Traits describe the texture of a person, and your values are the traits you believe are most worth cultivating and expressing.",
  },
  {
    question: "What does it mean to embody something?",
    answer:
      "To embody something means to make it a living part of how you move through the world — not just in theory, but in your actual choices, reactions, and habits. If courage is a trait you value, embodying it means actively choosing the braver path in small daily moments, not just in grand gestures.",
  },
  {
    question: "Can you give me an example?",
    answer:
      "Richard here - the founder of NoWastedDays. One of my values is 'Character'. I believe it is important to be a person of character; someone who is calm and resolute when times are hard but also grateful when the sailing is smoother.",
  },
];

const TRAIT_PLACEHOLDERS = [
  "Trait #1",
  "Trait #2",
  "Trait #3",
  "Trait #4",
  "Trait #5",
];

const REFLECTION_LABELS = {
  kant: "A rule for the world",
  mirror: "Part mirror, part dream",
  motivation: "What is motivating you?",
};

// ---------------------------------------------------------------------------
// SUB-COMPONENTS
// ---------------------------------------------------------------------------

function HelpAccordion({ items }) {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="values__help">
      {items.map((item, i) => (
        <div
          key={i}
          className={`values__help-item ${openIndex === i ? "open" : ""}`}
        >
          <button
            className="values__help-question"
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
          >
            <span>{item.question}</span>
            <FontAwesomeIcon
              icon={openIndex === i ? faChevronUp : faChevronDown}
              className="values__help-icon"
            />
          </button>
          <div className="values__help-answer-wrapper">
            <div className="values__help-answer">
              <p>{item.answer}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Editable version — used in review steps (3-5) and the final step
function EditableValuesSummary({ traits, onTitleChange, onDescriptionChange }) {
  const hasFilled = traits.some((t) => t.title.trim());
  if (!hasFilled) {
    return (
      <p className="values__summary-empty">
        Your traits will appear here as you add them.
      </p>
    );
  }
  return (
    <div className="values__summary values__summary--editable">
      {traits.map((trait, i) => {
        if (!trait.title.trim()) return null;
        return (
          <div
            key={i}
            className="values__summary-item values__summary-item--editable"
          >
            <input
              type="text"
              className="values__summary-title-input"
              value={trait.title}
              onChange={(e) => onTitleChange(i, e.target.value)}
              maxLength={40}
            />
            <textarea
              className="values__summary-desc-textarea"
              value={trait.description}
              onChange={(e) => onDescriptionChange(i, e.target.value)}
              placeholder="Add a description..."
              rows={2}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// STEP COMPONENTS
// ---------------------------------------------------------------------------

function StepIntro() {
  return (
    <div className="values__step-inner values__step-inner--single">
      <div className="values__step-header">
        <div className="values__step-header-top">
          <div className="values__step-icon"><ValuesIcon /></div>
          <span className="values__step-label">A guided exercise</span>
        </div>
        <h2>
          Why is articulating your values a positive and powerful thing to do?
        </h2>
      </div>
      <div className="values__step-body values__step-body--centered">
        <div className="values__body-icon"><ValuesIcon /></div>
        <p>
          Having a authentic and nicely articulated set of values is fantastic
          as it provides a phenomenal filter through which to pass our
          experiences and decisions. By having our values at hand, any moment in
          our life becomes a chance to put them into practice and there is a
          small but deeply significant mental reward you get every time you do
          this.
        </p>
        <p>
          Embodying your own values is both satisfying in the moment but also
          helps build deeper self-awareness and self-assurance.
        </p>
        <p>
          <strong>Let's get this rolling and move to the first step.</strong>
        </p>
      </div>
    </div>
  );
}

function StepTraits({ traits, onTitleChange }) {
  const filledCount = traits.filter((t) => t.title.trim()).length;

  return (
    <div className="values__step-inner values__step-inner--two-col">
      {/* Left: explanation */}
      <div className="values__col values__col--left">
        <div className="values__step-header">
          <div className="values__step-header-top">
            <div className="values__step-icon"><ValuesIcon /></div>
            <span className="values__step-label">Step one</span>
          </div>
          <h2>The traits that are most important to you.</h2>
        </div>
        <div className="values__step-body">
          <p>
            Each of us has a different experience of the world and uses language
            to describe things differently. This first step involves taking a
            moment to reflect on the traits that, based on your experience of
            life to date, you believe it is important for you to embody.
          </p>
          <p>Add between 3 and 5 traits to continue.</p>
          <HelpAccordion items={HELP_ITEMS} />
        </div>
      </div>

      {/* Right: inputs */}
      <div className="values__col values__col--right">
        <div className="values__traits-inputs">
          {traits.map((trait, i) => (
            <div key={i} className="values__trait-row">
              <span className="values__trait-num">0{i + 1}</span>
              <input
                type="text"
                className="values__trait-input"
                placeholder={TRAIT_PLACEHOLDERS[i]}
                value={trait.title}
                onChange={(e) => onTitleChange(i, e.target.value)}
                maxLength={40}
              />
            </div>
          ))}
          <p
            className={`values__trait-hint ${
              filledCount >= 3 ? "values__trait-hint--ready" : ""
            }`}
          >
            {filledCount >= 3
              ? `${filledCount} trait${
                  filledCount > 1 ? "s" : ""
                } added — ready to continue.`
              : `${filledCount} of 3 minimum traits added.`}
          </p>
        </div>
      </div>
    </div>
  );
}

function StepDescriptions({ traits, onDescriptionChange }) {
  const filled = traits.filter((t) => t.title.trim());

  return (
    <div className="values__step-inner values__step-inner--single">
      <div className="values__step-header">
        <div className="values__step-header-top">
          <div className="values__step-icon"><ValuesIcon /></div>
          <span className="values__step-label">Step two</span>
        </div>
        <h2>Bringing that trait to life.</h2>
      </div>
      <div className="values__step-body">
        <p className="values__intro-text">
          Now, with {filled.length} trait{filled.length !== 1 ? "s" : ""}{" "}
          articulated, let's start to bring them to life. Any trait — as a
          behaviour and as a word — will mean something different to each and
          every person. However, you picked it because it means something
          authentically and specifically to you as an individual. Imagine
          someone asked you,{" "}
          <em>
            "What do you mean by that value?{" "}
            {traits[0].title &&
              `What do you mean when you say ${traits[0].title.toLowerCase()} for example?"`}
            "
          </em>
          , and write your answers below each one. Don't worry if you can only
          think of one for the time being. You can always move back and forward
          inspired by the steps that follow this one.
        </p>
        <div className="values__descriptions-grid">
          {traits.map((trait, i) => {
            if (!trait.title.trim()) return null;
            return (
              <div key={i} className="values__description-block">
                <label className="values__description-label">
                  {trait.title}
                </label>
                <textarea
                  className="values__description-textarea"
                  placeholder={`What does "${trait.title}" mean to you, specifically?`}
                  value={trait.description}
                  onChange={(e) => onDescriptionChange(i, e.target.value)}
                  rows={3}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const REVIEW_STEP_CONFIG = {
  kant: {
    stepLabel: "Step three",
    title: "A rule for the world.",
    quote:
      "Act only on that maxim whereby you can at the same time will that it should become a universal law.",
    quoteAuthor: "Immanuel Kant",
    body: (
      <>
        <p>
          Immanuel Kant was a German philosopher who thought a lot about reason
          and morality, and what we can really understand of our lifes
          experiences. He has a great quote which you can see above and inspired
          by this, you are now going take a look at the traits and descriptions
          that are starting to emerge in front of you and see if they need any
          tweaking or changing.
        </p>
        <p>
          Ask yourself:{" "}
          <em style={{ fontWeight: "bold" }}>
            "If these were rules for the world to live by, would that be a world
            I would want to live in? What kind of world would it be?"
          </em>
        </p>
        <p>
          Use the space below to note any thoughts or adjustments that arise.
          You may decide that you need to complete rethink what you've done. You
          may decide that nothing needs to change that much. Both are good
          conclusions.
        </p>
      </>
    ),
    placeholder:
      "What kind of world would it be? Any thoughts or refinements...",
  },
  mirror: {
    stepLabel: "Step four",
    title: "Part mirror, part dream.",
    quote: null,
    body: (
      <>
        <p>
          With all this talk of traits and embodiment, it would be easy to think
          that your values were only a mirror of who you are today. To a huge
          degree that is true — but they also carry an important aspirational
          function; a reminder of the version of you that you'd like to grow
          into over time.
        </p>
        <p>
          They are there to help ground you in how you think and feel today, but
          also push you just beyond the boundaries of your existing ways of
          being, so you can — hopefully — be a little more virtuous, understand
          yourself a little deeper, become more of the person you'd like to be
          over time.
        </p>
        <p>
          Have a look at your values set.{" "}
          <em style={{ fontWeight: "bold" }}>
            Can you see a nice balance of very much the person you are today,
            alongside a touch of aspiration that will move you towards someone
            you'd like to evolve into?
          </em>
        </p>
      </>
    ),
    placeholder:
      "Reflections on balance — where are you today, where would you like to be?",
  },
  motivation: {
    stepLabel: "Step five",
    title: "What is motivating you?",
    quote: "Man considers the actions, but God weighs the intentions.",
    quoteAuthor: "Thomas à Kempis",
    body: (
      <>
        <p>
          This is not about letting religion enter the picture. It is simply
          checking in with the nature of your motivations i.e. what, if
          anything, to you hope to gain from having a clearer, well articulated
          set of values? There are no good or bad motivations necessarily;
          simply ones that are more likely to be healthy and others that are
          more likely to be unhealthy. On a matrix, you could plot your
          motivations as having a position on the extrinsic and instrinsic
          scale, and the selfish to sefless scale. My lived experience and
          learned hypothesis is that for a truly healthy outcome, you want it to
          lean towards the intrinsic, and balance both selfish and selfless
          considerations.
        </p>
        <p>
          For example, imagine you were very business-oriented and hoped your
          clearer values would help generate better returns. That is a very
          self-absorbed motivation worth addressing. Alternatively, a deeply
          spiritual motivation to create a more harmonious universe is very
          selfless — but there is nothing wrong with being a little more
          self-oriented than that either as something that existential can be
          mentally destructive as it can seem so far outside of your control.
        </p>
        <p>
          <em style={{ fontWeight: "bold" }}>
            What is genuinely motivating you here? Does your motivation need a
            bit of course correcting to protect against being too externally
            motivated and or selfish, or vaguely, nonspecifically selfless?
          </em>
        </p>
      </>
    ),
    placeholder: "What's really motivating you? Honest reflections welcome...",
  },
};

function StepReview({
  stepKey,
  traits,
  reflections,
  onReflectionChange,
  onTitleChange,
  onDescriptionChange,
}) {
  const config = REVIEW_STEP_CONFIG[stepKey];
  const stepIcon = stepKey === "mirror" ? <VisionIcon /> : <QuoteIconV2 />;

  return (
    <div className="values__step-inner values__step-inner--two-col">
      {/* Left: framing */}
      <div className="values__col values__col--left">
        <div className="values__step-header">
          <div className="values__step-header-top">
            <div className="values__step-icon">{stepIcon}</div>
            <span className="values__step-label">{config.stepLabel}</span>
          </div>
          <h2>{config.title}</h2>
        </div>
        <div className="values__step-body">
          {config.quote && (
            <blockquote className="values__quote">
              <p>"{config.quote}"</p>
              {config.quoteAuthor && <cite>— {config.quoteAuthor}</cite>}
            </blockquote>
          )}
          {config.body}
        </div>
      </div>

      {/* Right: editable values + reflection */}
      <div className="values__col values__col--right">
        <div className="values__review-panel">
          <p className="values__review-panel-label">Your values</p>
          <EditableValuesSummary
            traits={traits}
            onTitleChange={onTitleChange}
            onDescriptionChange={onDescriptionChange}
          />
          <div className="values__reflection-wrap">
            <label className="values__reflection-label">Your reflection</label>
            <textarea
              className="values__reflection-textarea"
              placeholder={config.placeholder}
              value={reflections[stepKey]}
              onChange={(e) => onReflectionChange(stepKey, e.target.value)}
              rows={4}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepPractice() {
  return (
    <div className="values__step-inner values__step-inner--single">
      <div className="values__step-header">
        <div className="values__step-header-top">
          <div className="values__step-icon"><ValuesIcon /></div>
          <span className="values__step-label">Step six</span>
        </div>
        <h2>Putting your values into practice.</h2>
      </div>
      <div className="values__step-body values__step-body--centered">
        <p>
          Like any new frame that we might look at the world through, it takes a
          moment to feel natural. To get yourself started, try to begin
          imagining your values as a new pair of sunglasses that you have in
          your bag.
        </p>
        <p>
          As you are moving through life, and you have even a shade of doubt
          about how to act or what is right in a particular moment, stick your
          values sunglasses on and see what your decision looks like through
          those lenses.
        </p>
        <p>
          Over time, your values and your values attuned response will happen
          more and more easily — until they feel less like a tool you reach for,
          and more like something you simply are.
        </p>
      </div>
    </div>
  );
}

function StepComplete({
  traits,
  reflections,
  submitStatus,
  onSave,
  onNavigate,
  onTitleChange,
  onDescriptionChange,
}) {
  const hasReflections = Object.entries(reflections).some(([, v]) => v.trim());

  return (
    <div className="values__step-inner values__step-inner--single">
      <div className="values__step-header values__step-header--celebration">
        <div className="values__step-header-top">
          <div className="values__step-icon"><ValuesIcon /></div>
          <span className="values__step-label">You've done it</span>
        </div>
        <h2>Your values, articulated.</h2>
      </div>
      <div className="values__step-body">
        <div className="values__body-icon values__body-icon--complete"><ValuesIcon /></div>
        <p className="values__complete-intro">
          Congratulations — you have taken the time to think carefully about who
          you are and who you want to be. Give your values a final look below,
          make any last edits, and when you are ready, save them to your Year
          Map.
        </p>

        {/* Editable final values */}
        <div className="values__complete-editable">
          <EditableValuesSummary
            traits={traits}
            onTitleChange={onTitleChange}
            onDescriptionChange={onDescriptionChange}
          />
        </div>

        {/* Reflections recap */}
        {hasReflections && (
          <div className="values__complete-reflections">
            <p className="values__complete-section-label">Your reflections</p>
            {Object.entries(reflections).map(([key, value]) => {
              if (!value.trim()) return null;
              return (
                <div key={key} className="values__complete-reflection-item">
                  <span className="values__complete-reflection-label">
                    {REFLECTION_LABELS[key]}
                  </span>
                  <p>{value}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Save action */}
        <div className="values__complete-actions">
          {submitStatus === "success" ? (
            <div className="values__complete-success">
              <FontAwesomeIcon icon={faCheck} />
              <span>Saved to your Year Map.</span>
            </div>
          ) : submitStatus === "no-year" ? (
            <div className="values__complete-notice">
              <p>
                You don't have a Year Map yet. You can{" "}
                <button className="values__link-btn" onClick={onNavigate}>
                  create one here
                </button>{" "}
                and your values will be waiting for you to add.
              </p>
            </div>
          ) : (
            <button
              className={`values__submit-btn ${
                submitStatus === "saving" ? "values__submit-btn--saving" : ""
              }`}
              onClick={onSave}
              disabled={submitStatus === "saving"}
            >
              {submitStatus === "saving" ? (
                "Saving..."
              ) : (
                <>
                  Save to Year Map <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          )}
          {submitStatus === "error" && (
            <p className="values__complete-error">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ANIMATION VARIANTS
// ---------------------------------------------------------------------------

const slideVariants = {
  enter: (dir) => ({
    x: dir > 0 ? "40px" : "-40px",
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (dir) => ({
    x: dir > 0 ? "-40px" : "40px",
    opacity: 0,
  }),
};

// ---------------------------------------------------------------------------
// MAIN SCREEN
// ---------------------------------------------------------------------------

export default function ValuesScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const yearList = useSelector((state) => state.yearList);
  const { years } = yearList;

  // --- Step state ---
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  // --- Values state ---
  const [traits, setTraits] = useState(
    Array(5)
      .fill(null)
      .map(() => ({ title: "", description: "" })),
  );
  const [reflections, setReflections] = useState({
    kant: "",
    mirror: "",
    motivation: "",
  });

  // --- Submission state ---
  const [submitStatus, setSubmitStatus] = useState("");

  // --- Effects ---
  useEffect(() => {
    dispatch(listYears());
  }, [dispatch, userInfo]);

  // --- Handlers ---
  const handleTraitTitleChange = (index, value) => {
    setTraits((prev) =>
      prev.map((t, i) => (i === index ? { ...t, title: value } : t)),
    );
  };

  const handleTraitDescriptionChange = (index, value) => {
    setTraits((prev) =>
      prev.map((t, i) => (i === index ? { ...t, description: value } : t)),
    );
  };

  const handleReflectionChange = (key, value) => {
    setReflections((prev) => ({ ...prev, [key]: value }));
  };

  const filledCount = traits.filter((t) => t.title.trim()).length;
  const canProceedFromTraits = filledCount >= 3;

  const canGoNext = () => {
    if (currentStep === 1) return canProceedFromTraits;
    return true;
  };

  const goNext = () => {
    if (!canGoNext() || currentStep >= TOTAL_STEPS - 1) return;
    setDirection(1);
    setCurrentStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (currentStep === 0) return;
    setDirection(-1);
    setCurrentStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Save to year map
  const handleSaveToYearMap = async () => {
    if (!years || years.length === 0) {
      setSubmitStatus("no-year");
      return;
    }

    const latestYearId = years[years.length - 1]._id;
    setSubmitStatus("saving");

    try {
      const fullYear = await dispatch(getYearByIdAction(latestYearId));
      const keys = ["one", "two", "three", "four", "five"];
      const formattedValues = {};
      traits.forEach((trait, i) => {
        if (trait.title.trim()) {
          formattedValues[keys[i]] = {
            title: trait.title.trim(),
            description: trait.description.trim(),
          };
        }
      });

      const updatedMeyear = {
        ...(fullYear || {}),
        values: formattedValues,
      };

      await dispatch(updateYearAction(latestYearId, updatedMeyear));
      setSubmitStatus("success");
    } catch (err) {
      console.error(err);
      setSubmitStatus("error");
    }
  };

  const handleNavigateToYear = () => {
    if (years && years.length > 0) {
      navigate(`/year/${years[years.length - 1]._id}`);
    } else {
      navigate("/year/create");
    }
  };

  // --- Render current step ---
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <StepIntro />;
      case 1:
        return (
          <StepTraits traits={traits} onTitleChange={handleTraitTitleChange} />
        );
      case 2:
        return (
          <StepDescriptions
            traits={traits}
            onDescriptionChange={handleTraitDescriptionChange}
          />
        );
      case 3:
        return (
          <StepReview
            stepKey="kant"
            traits={traits}
            reflections={reflections}
            onReflectionChange={handleReflectionChange}
            onTitleChange={handleTraitTitleChange}
            onDescriptionChange={handleTraitDescriptionChange}
          />
        );
      case 4:
        return (
          <StepReview
            stepKey="mirror"
            traits={traits}
            reflections={reflections}
            onReflectionChange={handleReflectionChange}
            onTitleChange={handleTraitTitleChange}
            onDescriptionChange={handleTraitDescriptionChange}
          />
        );
      case 5:
        return (
          <StepReview
            stepKey="motivation"
            traits={traits}
            reflections={reflections}
            onReflectionChange={handleReflectionChange}
            onTitleChange={handleTraitTitleChange}
            onDescriptionChange={handleTraitDescriptionChange}
          />
        );
      case 6:
        return <StepPractice />;
      case 7:
        return (
          <StepComplete
            traits={traits}
            reflections={reflections}
            submitStatus={submitStatus}
            onSave={handleSaveToYearMap}
            onNavigate={handleNavigateToYear}
            onTitleChange={handleTraitTitleChange}
            onDescriptionChange={handleTraitDescriptionChange}
          />
        );
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / TOTAL_STEPS) * 100;
  const isLastStep = currentStep === TOTAL_STEPS - 1;

  return (
    <>
      <SEO
        title="My Values"
        description="A guided exercise to help you articulate your personal values."
        canonicalUrl="https://nowasteddays.io/my-values"
      />
      <Header variant="light" active={true} />

      <main className="values__page">
        <div className="values__wrapper">
          {/* Progress */}
          <div className="values__progress-row">
            <div className="values__progress-track">
              <motion.div
                className="values__progress-fill"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
            </div>
            <span className="values__step-counter">
              {currentStep + 1} / {TOTAL_STEPS}
            </span>
          </div>

          {/* Card */}
          <div className="values__card">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="values__motion-wrapper"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          {!isLastStep && (
            <div className="values__nav">
              <button
                className="values__nav-btn values__nav-btn--back"
                onClick={goBack}
                disabled={currentStep === 0}
                aria-label="Go back"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                <span>Back</span>
              </button>

              <button
                className={`values__nav-btn values__nav-btn--next${
                  !canGoNext() ? " values__nav-btn--disabled" : ""
                }`}
                onClick={goNext}
                disabled={!canGoNext()}
                aria-label="Continue"
              >
                <span>{currentStep === 0 ? "Let's begin" : "Continue"}</span>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}

          {isLastStep && submitStatus !== "success" && (
            <div className="values__nav values__nav--end">
              <button
                className="values__nav-btn values__nav-btn--back"
                onClick={goBack}
                aria-label="Go back"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                <span>Back</span>
              </button>
            </div>
          )}
        </div>
      </main>

      <TestFooter />
    </>
  );
}
